import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { useActions } from "app/utils/hooks";
import { fetchGroupById, putGroupSettings } from "app/actions/groups";
import GroupSettingsLayout from "../GroupSettingsLayout";
import TeamGroupModel from "app/models/TeamGroupModel";
import ContentPanel from "app/components/Layouts/ContentPanel";
import SwitchToggle from "app/components/OptionButtonGroup/SwitchToggle";
import ButtonVariant from "app/components/Button/ButtonVariant";
import ChannelsPermissionsList from "../GroupSettings/_components/ChannelsPermissionsList";
import { useParams } from "react-router-dom";
import {
  GroupPermissions as GroupPermission,
  PermissionOption,
} from "app/models/GroupsModel";
import ChannelPermissionsModal from "../GroupSettings/_components/ChannelPermissionsModal";
import SuccessModal from "../GroupSettings/_components/SuccessModal";
import PermissionSkeleton from "../GroupSettings/_components/PermissionSkeleton";
import { fetchTeamGroupSettings } from "app/actions/team";
import { Toast } from "app/components/helpers";
import { removeTeamGroupSettings } from "app/actions/team-group";
import customPrompt from "app/components/inputs/SelectBox/helpers/customPrompt";

const GroupPermissions = () => {
  const { t } = useTranslation();

  const teamActions = useActions({
    fetchGroupById,
    putGroupSettings,
    fetchTeamGroupSettings,
    removeTeamGroupSettings,
  });
  const params = useParams<{ id: string }>();

  const [groupData, setGroupData] = useState<TeamGroupModel>({
    membersCanPost: false,
    membersCanReply: false,
    membersCanViewOtherMembers: false,
    membersCanSeePostData: false,
  });
  const [toastAlert, setToastAlert] = useState(null);
  const [toastErrorAlert, setToastErrorrAlert] = useState(null);
  const [pendingUpdate, setPendingUpdate] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [channelPermissionsList, setChannelPermissionsList] = useState<
    TeamGroupModel[]
  >([]);

  const [permissionModal, setPermissionModal] = useState(false);
  const [successModal, setSuccessModal] = useState<{
    open: boolean;
    data: GroupPermission | null;
  }>({
    open: false,
    data: null,
  });

  const fetchGroup = () => {
    setLoadingData(true);
    const groupId = params?.id;
    teamActions
      .fetchGroupById(groupId)
      .then((data: TeamGroupModel) => {
        setGroupData(data);
        setLoadingData(false);
      })
      .catch((err) => {
        console.error(err);
        setLoadingData(false);
      })
      .finally(() => {
        setLoadingData(false);
      });
  };

  const fetchSettings = async () => {
    try {
      const response = await teamActions.fetchTeamGroupSettings(params?.id);
      setChannelPermissionsList(response);

      setLoadingData(false);
    } catch (err) {
      setLoadingData(false);
      console.error(err);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchGroup();
    fetchSettings();
  }, []);

  const handleToggleChange = (key: keyof TeamGroupModel) => {
    const prevGroup = { ...groupData };
    const updatedValue = !groupData[key];
    setPendingUpdate(true);

    const updatedGroup = {
      ...groupData,
      [key]: updatedValue,
    };

    setGroupData(updatedGroup);

    teamActions
      .putGroupSettings(updatedGroup)
      .then(() => {
        teamActions.fetchGroupById(updatedGroup.id).then((res) => {
          setGroupData(res);

          setPendingUpdate(false);
        });
      })
      .catch((err) => {
        console.error(err);
        setGroupData(prevGroup);
        setPendingUpdate(false);
      });
  };

  const handlePermissionModal = () => {
    setPermissionModal((prev) => !prev);
  };

  const AllowList: PermissionOption[] = [
    {
      label: t("Allow members to create posts"),
      value: "membersCanPost",
      isActive: groupData?.membersCanPost || false,
    },
    {
      label: t("Allow members to reply to posts"),
      value: "membersCanReply",
      isActive: groupData?.membersCanReply || false,
    },
    {
      label: t("Allow members to view other members"),
      value: "membersCanViewOtherMembers",
      isActive: groupData?.membersCanViewOtherMembers || false,
    },
    {
      label: t("Allow members to see post Views/Likes"),
      value: "membersCanSeePostData",
      isActive: groupData?.membersCanSeePostData || false,
    },
  ];

  const openSuccessModal = (data: GroupPermission) => {
    setSuccessModal({
      open: true,
      data,
    });
  };

  const onAddPermission = (value: GroupPermission) => {
    handlePermissionModal();
    fetchSettings();
    openSuccessModal(value);
  };

  const closeSuccessModal = () => {
    setSuccessModal({
      open: false,
      data: null,
    });
  };

  const excludeChannelIds = useMemo(
    () =>
      (channelPermissionsList ?? []).map((item) => {
        return item.teamGroupId;
      }),
    [channelPermissionsList],
  );

  const updateData = (value: TeamGroupModel) => {
    setChannelPermissionsList((prev) =>
      prev.map((item) => (item?.id === value?.id ? value : item)),
    );
  };

  const onError = () => {
    setToastErrorrAlert(t("Something went wrong. Please try again later"));
  };

  const onRemoveChannelPermissions = useCallback(
    (channelData: GroupPermission) => {
      customPrompt({
        message: t(
          `Are you sure you would like to delete permissions for {{teamGroup}}?`,
          {
            teamGroup: channelData?.teamGroupName,
          },
        ),
        onYes: async () => {
          try {
            await teamActions.removeTeamGroupSettings(channelData?.teamGroupId);
            setChannelPermissionsList((prev) =>
              prev.filter((a) => a.teamGroupId !== channelData.teamGroupId),
            );
            setToastAlert(
              t(`Permissions for {{teamGroup}} have been removed.`, {
                teamGroup: channelData?.teamGroupName,
              }),
            );
          } catch (err) {
            console.error('Channel permissions couldn"t be removed:', err);
            setToastErrorrAlert(
              t("Something went wrong. Please try again later"),
            );
          }
        },
        onCancel: () => {
          return;
        },
      });
    },
    [],
  );

  return (
    <GroupSettingsLayout>
      <ContentPanel>
        <div className={styles.container}>
          <h3 className={styles.panel_title}>{t("Permissions")}</h3>
          <div className={styles.content_wrapper}>
            <div className={styles.permissions_container}>
              <label className={styles.label}>{t("Group Permissions")}</label>
              <p className={styles.permissions_description}>
                {t(
                  "The group permissions will be applied to all channels by default.",
                )}
              </p>
              <div className={styles.switch_permissions_wrapper}>
                {AllowList.map((item) => {
                  if (loadingData) {
                    return <PermissionSkeleton />;
                  }
                  return (
                    <SwitchToggle
                      key={item.value}
                      checked={item.isActive}
                      disabled={pendingUpdate}
                      onChange={() =>
                        handleToggleChange(item.value as keyof TeamGroupModel)
                      }
                      title={item.label}
                    />
                  );
                })}
              </div>
              <hr className={styles.border} />
              <div className={styles.permissions_section}>
                <div className={styles.channels_permissions}>
                  <label className={styles.label}>
                    {t("Channels permissions")}
                  </label>
                  <p className={styles.permissions_description}>
                    {t(
                      "You can assign specific permissions to individual channels. If you don't, the group permissions will be applied to all channels by default.",
                    )}
                  </p>
                </div>

                <ChannelsPermissionsList
                  data={channelPermissionsList}
                  loading={loadingData}
                  updateData={updateData}
                  onRemovePermission={onRemoveChannelPermissions}
                />

                <ButtonVariant
                  title={t("Add Channel Permissions")}
                  buttonType="secondary-filled"
                  childStyles={styles.add_btn_inner}
                  icon={<i className="ico ico-add" />}
                  buttonStyles={styles.add_btn}
                  onClickFunc={handlePermissionModal}
                />
              </div>
            </div>
          </div>
        </div>

        {permissionModal && (
          <ChannelPermissionsModal
            isOpen={permissionModal}
            onCloseFunc={handlePermissionModal}
            onSaveChannelPermissions={onAddPermission}
            onErrorSavingPermissions={onError}
            excludeChannelIds={excludeChannelIds}
          />
        )}
        {successModal.open && (
          <SuccessModal
            isOpen={successModal.open}
            data={successModal.data}
            onClose={closeSuccessModal}
          />
        )}

        <Toast
          error={toastErrorAlert}
          success={toastAlert}
          onDone={() => setToastAlert(null)}
        />
      </ContentPanel>
    </GroupSettingsLayout>
  );
};

export default GroupPermissions;
